import React from "react";
import {
  Camera, Users, Megaphone, FileCheck, FileSignature, Sparkles,
  Wrench, Building2, LogOut, Flame, Zap, Wind, BadgeCheck,
  Check, ShieldCheck, Phone, Mail, MapPin, Globe, Info,
} from "lucide-react";
import { DEFAULT_FEES, rateFor, arrangementLabel } from "../lib/termsFees";

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">{children}</p>
);

const Section = ({ eyebrow, title, children, className = "" }) => (
  <div className={`mt-8 rounded-2xl border border-[#e5ddd0] bg-white p-7 shadow-sm lg:p-9 ${className}`}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    {title && (
      <>
        <h2 className="mt-2 font-display text-2xl font-600 text-[#16323f]">{title}</h2>
        <span className="mt-3 block h-[3px] w-14 rounded-full bg-[#eaa32a]" />
      </>
    )}
    <div className="mt-5 space-y-4 text-sm leading-relaxed text-[#5b5450]">{children}</div>
  </div>
);

const Yes = () => (
  <span className="inline-grid h-6 w-6 place-items-center rounded-full bg-[#35a4a4] text-white">
    <Check size={14} strokeWidth={3} />
  </span>
);

const Callout = ({ children }) => (
  <div className="flex items-start gap-3 rounded-xl bg-[#f3ede3] p-4 text-sm leading-relaxed text-[#5b5450]">
    <Info size={18} className="mt-0.5 shrink-0 text-[#c9a25e]" />
    <div>{children}</div>
  </div>
);

const Clause = ({ n, title, children }) => (
  <div className="rounded-xl border border-[#eee6da] bg-[#faf7f0] p-5">
    <h3 className="font-display text-base font-600 text-[#16323f]">
      {n != null && <span className="mr-2 text-[#ec008c]">{n}.</span>}{title}
    </h3>
    <div className="mt-2 space-y-3">{children}</div>
  </div>
);

const tiersFor = (fees) => ["Full Management", "Rent Collection", "Let Only"].map((name) => ({
  name, commission: rateFor(fees, name), fee: arrangementLabel(fees, name),
}));

const arrClause = (fees) => {
  const a = String(fees.arrangement_fee ?? "").trim();
  return a === "" || a === "0" ? "" : `, plus a £${a} arrangement fee`;
};

const scheduleFor = (fees) => [
  [`Let Only — ${rateFor(fees, "Let Only")}`, `Sole agency basis. ${rateFor(fees, "Let Only")} of the gross rent payable for the initial letting period${arrClause(fees)}. Normally deducted from the first rent payment; any balance is payable in cleared funds before the tenancy commences.`],
  [`Rent Collection — ${rateFor(fees, "Rent Collection")}`, `Sole agency basis. ${rateFor(fees, "Rent Collection")} of the gross rent, premium or other money payable throughout the tenancy, taken monthly direct from the rent${arrClause(fees)}.`],
  [`Full Management — ${rateFor(fees, "Full Management")}`, `${rateFor(fees, "Full Management")} of the gross rent payable throughout the tenancy, taken monthly direct from the rent. The arrangement fee is included at no extra charge.`],
];

const INCLUDED = [
  { icon: Camera, title: "Professional Photography", body: "High-quality images to showcase your property at its best and attract the strongest applicants." },
  { icon: Users, title: "Accompanied Viewings", body: "We accompany every applicant and provide you with marketing feedback and regular updates." },
  { icon: Megaphone, title: "Comprehensive Marketing", body: "Full marketing campaign across all major portals and our own channels to maximise exposure." },
  { icon: FileCheck, title: "Referencing & Right to Rent", body: "Comprehensive tenant referencing, credit checks and statutory Right to Rent verification." },
  { icon: FileSignature, title: "Tenancy Agreement", body: "Paperless legal process — tenancy agreement prepared and executed electronically." },
  { icon: Sparkles, title: "Cleaning & Compliance", body: "Professional cleaning arranged on request; certification and compliance checks coordinated." },
];

const TENANT_ROWS = [
  ["Section 13 rent-increase notice", true, true, "£150 + VAT"],
  ["Managing a rent review dispute", true, true, "£500 + VAT"],
  ["Deposit registration", true, true, true],
  ["Rent arrears advice", true, true, false],
  ["Tenancy protection service", true, true, false],
  ["Monthly & yearly financial statements", true, true, false],
  ["Collection of rent", true, true, false],
  ["Arrangement of other outgoings", true, true, false],
];

const INVENTORY = [
  ["0–1 bed", "£65", "£125", "£168"],
  ["2 bed", "£80", "£150", "£202"],
  ["3 bed", "£95", "£165", "£232"],
  ["4 bed", "£110", "£175", "£258"],
  ["5 bed", "£125", "£215", "£283"],
  ["6 bed+", "£140", "£230", "£307"],
];

const FM_BENEFITS = [
  { icon: Wrench, title: "Repairs & Maintenance", items: ["Instruction of repairs", "Recommended contractors", "24-hour emergency service", "Key-holding service"] },
  { icon: Building2, title: "Property Oversight", items: ["Periodic property check-ups", "Legal notices", "Utility management service", "Management fees paid monthly"] },
  { icon: LogOut, title: "End of Tenancy", items: ["Management of the check-out", "Deposit deduction advice", "Financial statements", "Smooth re-letting process"] },
];

const SAFETY = [
  { icon: Flame, title: "Furniture & Fire Safety", clause: "Clause 21", body: "It is a criminal offence to let with furniture or soft furnishings that cannot be proven to comply with the Furniture and Furnishings (Fire)(Safety) Regulations 1988 (as amended). You authorise us to remove any non-compliant item. The Regulations apply to upholstered furniture, beds, mattresses, cushions, pillows, cots and garden furniture that may be used indoors." },
  { icon: Zap, title: "Electrical Safety", clause: "Clause 22", body: "Electrical installations must be inspected and tested by a qualified person at least every 5 years under the Electrical Safety Standards (Private Rented Sector)(England) Regulations 2020. A copy of the EICR must be provided to the tenant and to us; remedial work completed within 28 days. We arrange this — you only pay the electrician's fee." },
  { icon: Wind, title: "Gas Safety", clause: "Clause 23", body: "It is a criminal offence to let with gas appliances not checked by a Gas Safe registered engineer within the previous 12 months. You must provide a valid Gas Safety Certificate or authorise us to arrange one (you only pay the engineer's fee). The GSC is renewed every 12 months. A tenancy cannot commence until we hold a valid GSC." },
  { icon: BadgeCheck, title: "Energy Performance", clause: "Clause 24", body: "You must provide a valid EPC before an applicant views or receives written details — failing to do so is a criminal offence. We can instruct an assessor (you only pay their fee). Under MEES, the property must not be let with an EPC rating below E unless a valid exemption is registered." },
];

export default function TermsContent({ fees = DEFAULT_FEES, lockedService = "" }) {
  const tiers = lockedService ? tiersFor(fees).filter((t) => t.name === lockedService) : tiersFor(fees);
  const schedule = lockedService ? scheduleFor(fees).filter(([t]) => t.startsWith(lockedService)) : scheduleFor(fees);
  return (
    <div>
      {/* Intro */}
      <Section eyebrow="New Space" title="Terms of Business">
        <p>
          This Agreement sets out the services New Space provides to you and your responsibilities as
          Landlord. The letting or management of your property cannot proceed until these Terms have been
          accepted. Please read it carefully — if any part isn't clear, speak to us before accepting. You
          will be bound by this Agreement as soon as you accept it, whether online or in writing.
        </p>
        <p>
          The easiest way to accept is online — tell us which service you'd like and a little about the
          property and yourself, and sign electronically below. It takes about ten minutes and reaches us
          instantly. If you'd rather not complete this online, call us on{" "}
          <a href="tel:02072752040" className="font-semibold text-[#ec008c]">020 7275 2040</a> or email{" "}
          <a href="mailto:website@newspaceuk.com" className="font-semibold text-[#ec008c]">website@newspaceuk.com</a>{" "}
          and we'll send a paper copy to sign and return.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl bg-[#f3ede3] p-5">
            <Eyebrow>Registered Details</Eyebrow>
            <p className="mt-2 text-sm text-[#16323f]">
              Managed Exit Ltd t/a New Space<br />266 Kingsland Road, London E8 4DG<br />
              Reg. No. 06631310 · VAT 975741674
            </p>
          </div>
          <div className="rounded-xl bg-[#f3ede3] p-5">
            <Eyebrow>Regulatory Membership</Eyebrow>
            <p className="mt-2 text-sm text-[#16323f]">
              Redress: The Property Ombudsman<br />
              Client Money Protection: UKALA, membership no. 18882
            </p>
          </div>
        </div>
      </Section>

      {/* What's in this agreement */}
      <Section eyebrow="Overview" title="What's in this Agreement">
        <div className="grid gap-4 sm:grid-cols-2">
          {[
            ["Schedule 1", "Our commission and fees."],
            ["Schedule 2", "General terms that apply to every instruction."],
            ["Schedule 3", "Additional terms for Rent Collection and Full Management."],
            ["Schedule 4", "How we collect your property, contact and payment details."],
          ].map(([s, d]) => (
            <div key={s} className="flex items-start gap-3 rounded-xl border border-[#eee6da] p-4">
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#16323f] font-display text-xs font-700 text-white">
                {s.split(" ")[1]}
              </span>
              <div>
                <p className="font-display text-sm font-600 text-[#16323f]">{s}</p>
                <p className="text-sm text-[#7a716a]">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Services & fees at a glance */}
      <Section eyebrow="At a glance" title="Services & Fees">
        <p>{lockedService ? `Your agreed ${lockedService} rate is shown below. All fees are shown exclusive of VAT unless otherwise stated.` : "A quick comparison of our three service levels. All fees are shown exclusive of VAT unless otherwise stated."}</p>
        <div className={`grid gap-4 ${tiers.length === 1 ? "sm:max-w-sm" : "sm:grid-cols-3"}`}>
          {tiers.map((t) => (
            <div key={t.name} data-testid={`fee-tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-2xl border p-6 text-center ${lockedService ? "border-[#ec008c] bg-[#ec008c]/[0.04]" : "border-[#e5ddd0] bg-[#faf7f0]"}`}>
              <p className="font-display text-base font-600 text-[#16323f]">{t.name}</p>
              <p className="mt-2 font-display text-3xl font-700 text-[#ec008c]">{t.commission}</p>
              <p className="mt-2 text-xs text-[#7a716a]">{t.fee}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Included with every service */}
      <Section eyebrow="Standard on every instruction" title="What's Included with Every Service">
        <p>Regardless of the service level you choose, every instruction includes the following as standard — from the moment we begin marketing through to the tenancy commencing.</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {INCLUDED.map((f) => (
            <div key={f.title} className="rounded-xl border border-[#eee6da] bg-[#faf7f0] p-5">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-[#16323f] text-[#eaa32a]">
                <f.icon size={20} />
              </span>
              <p className="mt-3 font-display text-sm font-600 text-[#16323f]">{f.title}</p>
              <p className="mt-1 text-sm text-[#7a716a]">{f.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Once there's a tenant comparison */}
      <Section eyebrow="Service comparison" title="Once There's a Tenant">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="bg-[#16323f] text-white">
                <th className="px-4 py-3 text-left font-600">Service</th>
                <th className="px-4 py-3 text-center font-600">Full Management</th>
                <th className="px-4 py-3 text-center font-600">Rent Collection</th>
                <th className="px-4 py-3 text-center font-600">Let Only</th>
              </tr>
            </thead>
            <tbody>
              {TENANT_ROWS.map((r, i) => (
                <tr key={i} className={i % 2 ? "bg-[#faf7f0]" : "bg-white"}>
                  <td className="border-b border-[#eee] px-4 py-3 font-medium text-[#16323f]">{r[0]}</td>
                  {r.slice(1).map((c, j) => (
                    <td key={j} className="border-b border-[#eee] px-4 py-3 text-center">
                      {c === true ? <Yes /> : c === false ? <span className="text-[#c9b8a8]">—</span> : <span className="text-[#16323f]">{c}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Full management exclusive benefits */}
      <Section eyebrow="Full Management" title="Exclusive Benefits">
        <p>Our Full Management service goes far beyond finding a tenant. Once the tenancy is in place, we take responsibility for the day-to-day running of your property so you don't have to.</p>
        <div className="grid gap-4 sm:grid-cols-3">
          {FM_BENEFITS.map((b) => (
            <div key={b.title} className="rounded-xl border border-[#eee6da] bg-[#faf7f0] p-5">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-[#35a4a4] text-white">
                <b.icon size={20} />
              </span>
              <p className="mt-3 font-display text-sm font-600 text-[#16323f]">{b.title}</p>
              <ul className="mt-3 space-y-2">
                {b.items.map((it) => (
                  <li key={it} className="flex items-center gap-2 text-sm text-[#5b5450]">
                    <Check size={14} className="text-[#35a4a4]" strokeWidth={3} /> {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#7a716a]">Prices exclude VAT unless stated. Full terms for every service and fee are set out in Schedules 1–3.</p>
      </Section>

      {/* Schedule 1 */}
      <Section eyebrow="Schedule 1" title="Commissions & Additional Fees">
        <Clause n={1} title="Commissions">
          <p>You are responsible for paying our commission when any person, company or other organisation enters into a binding contract for the occupation of the Premises as a result of: (a) a viewing conducted by New Space; (b) sight of any marketing or advertising material produced by New Space; or (c) an introduction from an existing occupier for whom we have previously charged a commission. Our commission is payable whether or not we are the effective cause of the transaction.</p>
          <p>VAT is chargeable on all commission at the prevailing rate (currently 20%); all fees are shown exclusive of VAT unless stated otherwise. If our fees are not paid on commencement of the tenancy, interest accrues daily at 4% above the Bank of England base rate.</p>
        </Clause>
        <div className={`grid gap-4 ${schedule.length === 1 ? "" : "sm:grid-cols-3"}`}>
          {schedule.map(([t, d]) => (
            <div key={t} className="rounded-xl border border-[#eee6da] bg-white p-5">
              <p className="font-display text-sm font-600 text-[#ec008c]">{t}</p>
              <p className="mt-2 text-sm text-[#5b5450]">{d}</p>
            </div>
          ))}
        </div>
        <Callout>
          In line with the Tenant Fees Act 2019, the only sums we charge a tenant are a holding deposit
          (capped at one week's rent), the security deposit, and the limited default charges the Act permits.
          We do not charge tenants a letting, renewal or administration fee.
        </Callout>
        <div className="grid gap-4 sm:grid-cols-2">
          <Clause n={2} title="Commission Refunds">
            <p>If the tenancy is terminated before the end of its full term, we refund commission you have paid on a pro-rata basis. This refund is only payable if we re-let the property, and is applied as a credit against the fee for the new letting.</p>
          </Clause>
          <Clause n={3} title="Additional Fees">
            <p>Referrals to a rent assessment committee, court or tribunal proceedings, or protracted correspondence on your behalf, are charged at £75 + VAT per hour. If specialist solicitors are instructed, you are also responsible for their fees and expenses.</p>
            <p><strong>Keys:</strong> one set per tenant plus an additional set retained at the branch; duplicates cut at your expense if not provided. <strong>Property attendance</strong> (other than scheduled visits) is £40 for up to one hour and £20 per hour or part thereafter.</p>
          </Clause>
          <Clause n={4} title="Sales Commission">
            <p>We do not charge a sales commission if a tenant purchases the Premises from you, or if you sell to a third party.</p>
          </Clause>
          <Clause n={5} title="Sub-Agency">
            <p>Where it would help let the Premises promptly, we will share details with our local panel of authorised agents on a commission-sharing basis, at no additional expense to you.</p>
          </Clause>
          <Clause n={6} title="Reimbursement of Fees and Costs">
            <p>By accepting this Agreement you consent to all outstanding fees, costs and other charges being deducted from funds we hold for any property you own, including any deposit deductions the tenant has agreed to pay you.</p>
          </Clause>
        </div>
      </Section>

      {/* Schedule 2 general terms */}
      <Section eyebrow="Schedule 2" title="General Terms">
        <div className="grid gap-4 sm:grid-cols-2">
          <Clause n={7} title="Initial Visit">
            <p>We will visit the Premises to view them and give you an indication of the likely market rent achievable.</p>
          </Clause>
          <Clause n={8} title="Prior to Letting">
            <p>You must ensure the Premises are fit to let. All machines, gas appliances and electrical goods should be in full working order, recently serviced, checked for safety, and have clear instructions for use.</p>
          </Clause>
          <Clause n={9} title="Cleaning">
            <p>Properties should be thoroughly cleaned and gardens (if applicable) in good seasonal condition. On request, we can arrange cleaning of the Premises.</p>
          </Clause>
          <Clause n={10} title="Marketing">
            <p>We will market your Premises to inform suitable applicants of its availability.</p>
          </Clause>
          <Clause n={11} title="Viewings">
            <p>As and when we have interested applicants, we will accompany them to the Premises using keys you provide.</p>
          </Clause>
          <Clause n={12} title="References">
            <p>We reference applicants for you: a draft sample tenancy agreement, references, a credit report checking financial standing and income, contacting the previous landlord where possible, and a personal referee — without responsibility for the accuracy of those references unless due to our negligence or breach of contract.</p>
          </Clause>
        </div>
        <Clause n={13} title="Inventories">
          <p>An inventory and schedule of condition is essential for proper management, whether let furnished or unfurnished, and reduces the risk of a deposit dispute. Without one, you may be unable to prove the condition of the Premises at the start of the tenancy or obtain compensation, and we have no liability for any resulting loss.</p>
          <div className="overflow-x-auto">
            <table className="mt-2 w-full min-w-[480px] border-collapse text-sm">
              <thead>
                <tr className="bg-[#16323f] text-white">
                  <th className="px-4 py-3 text-left font-600">Property size</th>
                  <th className="px-4 py-3 text-center font-600">Inspection</th>
                  <th className="px-4 py-3 text-center font-600">Check-in/out</th>
                  <th className="px-4 py-3 text-center font-600">Inventory & Check-in</th>
                </tr>
              </thead>
              <tbody>
                {INVENTORY.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-[#faf7f0]" : "bg-white"}>
                    <td className="border-b border-[#eee] px-4 py-3 font-medium text-[#16323f]">{r[0]}</td>
                    <td className="border-b border-[#eee] px-4 py-3 text-center">{r[1]}</td>
                    <td className="border-b border-[#eee] px-4 py-3 text-center">{r[2]}</td>
                    <td className="border-b border-[#eee] px-4 py-3 text-center">{r[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#7a716a]">Prices exclude VAT. We recommend an inventory and check-in for every new tenancy to protect your position at the end of the term.</p>
        </Clause>
        <div className="grid gap-4 sm:grid-cols-2">
          <Clause n={14} title="Service of Notice">
            <p>Unless the tenant voluntarily surrenders possession, a valid statutory notice is required. The Renters' Rights Act 2025 abolished Section 21 "no-fault" notices from 1 May 2026, so tenancies we arrange or manage are assured tenancies and possession may only be sought on the statutory Section 8 grounds (Housing Act 1988). We will advise on the grounds available and, where instructed, prepare and serve the relevant notice. If the tenant does not vacate, County Court proceedings may be necessary; we can introduce specialist solicitors.</p>
          </Clause>
          <Clause n={15} title="Utilities and Council Tax">
            <p>Where we manage the property, we notify the local authority when the tenant occupies. You are responsible for charges up to and including the occupation date, and for any void period. Many utility suppliers will not take instructions from us, so it remains your responsibility to contact them directly.</p>
          </Clause>
          <Clause n={16} title="Rent Review & Rent Increases">
            <p>Following the Renters' Rights Act 2025, tenancies continue on a periodic basis and fixed terms are no longer offered for new lettings. For managed / rent-collection tenancies we contact you to discuss a rent review — included in your fee. For Let Only we can issue a statutory Section 13 notice for £150 + VAT, and manage a rent review dispute for £500 + VAT. The tenant may challenge a proposed increase at the First-tier Tribunal; our fees remain payable for as long as the tenant occupies.</p>
          </Clause>
          <Clause n={17} title="Consent for Letting">
            <p>On request you must provide documentary evidence that you are legally entitled to grant a tenancy. By accepting these Terms you warrant that you are the owner or otherwise lawfully entitled. If registered with the Land Registry, we reserve the right to obtain office copy entries at your cost.</p>
          </Clause>
          <Clause n={18} title="Mortgages">
            <p>Where mortgaged, you need your mortgagee's written consent before the tenancy starts, and any special conditions must be given to us beforehand — they cannot be imposed on a tenant later. By accepting these Terms you warrant you have obtained the necessary consent.</p>
          </Clause>
          <Clause n={19} title="Sub-Letting">
            <p>If you are a leaseholder, you will normally need consent from your superior landlord, freeholder or managing agent before sub-letting. We will need a copy of the head lease and any schedules referred to in it.</p>
          </Clause>
          <Clause n={20} title="Insurance">
            <p>The Premises and contents listed in the inventory must be adequately insured, and your insurers must know the Premises are let — failing to tell them may invalidate cover. Check third-party liability cover is included. We recommend a policy covering loss of rent, contents and legal expenses.</p>
          </Clause>
        </div>
      </Section>

      {/* Safety & compliance */}
      <Section eyebrow="Schedule 2 · Clauses 21–24" title="Safety & Compliance Obligations">
        <p>As a landlord you have statutory safety obligations — failure to comply is a criminal offence in several areas. New Space arranges the checks as part of our service; you only pay the engineer's or assessor's own fee.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {SAFETY.map((s) => (
            <div key={s.title} className="rounded-xl border border-[#eee6da] bg-[#faf7f0] p-5">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-[#16323f] text-[#eaa32a]"><s.icon size={18} /></span>
                <div>
                  <Eyebrow>{s.clause}</Eyebrow>
                  <p className="font-display text-sm font-600 text-[#16323f]">{s.title}</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-[#5b5450]">{s.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Deposits, arrears, taxation */}
      <Section eyebrow="Schedule 2 · Clauses 25–31" title="Deposits, Rent & Accounts">
        <div className="grid gap-4 sm:grid-cols-2">
          <Clause n={25} title="Advance Rental Payment">
            <p>We collect one calendar month's rent in advance from the proposed tenant on signature of the tenancy agreement, paid to you under the Payment of Landlord Accounts clause.</p>
          </Clause>
          <Clause n={26} title="Rent Arrears / Breaches">
            <p>It is your responsibility to take all necessary steps to protect your interests, including instructing solicitors and commencing proceedings to recover arrears and defend actions brought against you.</p>
          </Clause>
        </div>
        <Clause n={27} title="Security Deposits">
          <p>We collect a 5-week security deposit where annual rent is under £50,000, or a 6-week deposit where it is £50,000 or more, in line with the Tenant Fees Act 2019. The deposit is held by us as stakeholder in an interest-bearing account; any interest is retained to cover banking and administration costs. As stakeholder we cannot release the deposit without the written consent of both parties.</p>
          <p>After the tenancy ends, with the tenant's written consent, you may ask us to deduct: damage caused by the tenant; damage or cleaning due to pets; housing benefit repayable to the local authority; unpaid rent; and unpaid utility or similar accounts. We are members of The Deposit Protection Scheme (depositprotection.com). Registering your deposit is included, whichever service you use.</p>
          <Callout>
            Since April 2007, no landlord may hold a deposit for a tenancy of this kind unless they, or their agent,
            belong to a government-approved scheme. If New Space does not manage the Premises, it is your
            responsibility to protect the deposit and serve the Prescribed Information — failing to do so could make
            you liable to a penalty of up to three times the deposit, plus legal costs.
          </Callout>
        </Clause>
        <div className="grid gap-4 sm:grid-cols-2">
          <Clause n={28} title="Taxation">
            <p>You are liable for tax on your rental income and must inform HMRC that you are letting. Keep invoices for six years. <strong>Non-Resident Landlords:</strong> until an HMRC approval number is received, we must deduct tax at the prevailing rate (currently 20%) and forward it quarterly — a £250 + VAT p.a. admin charge applies. <strong>Making Tax Digital:</strong> from 6 April 2026, landlords with gross rental income over £50,000 must keep digital records and send quarterly updates to HMRC.</p>
          </Clause>
          <Clause n={29} title="Payment of Landlord Accounts">
            <p>Accounts are paid within 10 working days of the tenancy commencing, transferred electronically to the bank details you provide. Rent received is processed and paid out within 5 working days of receipt and clearance.</p>
          </Clause>
          <Clause n={30} title="Statements & Correspondence">
            <p>To reduce our environmental impact, statements and correspondence are emailed rather than posted — please ensure your email address is correct. You also receive a login to our online landlord portal to see all your documents in one place, any time.</p>
          </Clause>
          <Clause n={31} title="Houses in Multiple Occupation">
            <p>Mandatory licensing applies where a property is occupied by five or more people forming two or more households sharing kitchen, bathroom or toilet facilities. Additional and selective licensing may also apply locally, and planning consent may be required. It is your responsibility to investigate, obtain and comply with any licensing or planning permission required.</p>
          </Clause>
        </div>
      </Section>

      {/* Schedule 3 */}
      <Section eyebrow="Schedule 3" title="Rent Collection & Full Management">
        <Clause n={32} title="Rent Collection">
          <p>We use our best efforts to arrange a standing order so the tenant pays rent direct to us. We send a monthly statement within 10 working days of receiving cleared funds, less agreed fees and expenses. We cannot be held responsible if the tenant fails to pay, unless due to our negligence or breach of contract — but we will act in your name to recover arrears and, if needed, advise you to instruct specialist solicitors (you remain responsible for legal costs).</p>
          <div className="mt-2 rounded-xl bg-[#f3ede3] p-4">
            <p className="font-display text-sm font-600 text-[#16323f]">Rent Guarantee Policy (optional)</p>
            <ul className="mt-2 space-y-2">
              {["One policy per property regardless of the number of tenants", "Protects the monthly rent", "Includes legal expenses for serving notices and gaining vacant possession", "Cover continues until vacant possession is obtained", "Tenants can be changed during the tenancy subject to successful referencing"].map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm text-[#5b5450]"><Check size={14} className="mt-1 shrink-0 text-[#35a4a4]" strokeWidth={3} /> {it}</li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-[#7a716a]">Subject to all tenants and any guarantors passing a reference. Please ask us for a quote.</p>
          </div>
        </Clause>
        <div className="grid gap-4 sm:grid-cols-2">
          <Clause n={33} title="Maintenance of the Premises">
            <p>You authorise us to organise minor repairs up to £250 including VAT. We retain £200 from your first rental payment as a repair fund. Where repairs are likely to exceed £250 + VAT we seek your written authority; if we don't hear from you within 3 days we proceed using our judgement. A supervision charge of 10% (minimum £250 + VAT) applies on protracted refurbishment or building works. We cannot arrange works without first holding sufficient cleared funds.</p>
          </Clause>
          <Clause n={34} title="Payment of Other Outgoings">
            <p>Where instructed and provided we hold sufficient funds, we can pay other outgoings from your rental income — for example ground rent and service charges where you hold a leasehold interest. Please instruct those providers to contact us directly.</p>
          </Clause>
          <Clause n={35} title="Income & Expenditure Summary">
            <p>Within three months of the tax year ending, we send a summary of the income and expenditure associated with letting your property, to help with your Self-Assessment. This is not intended as tax advice.</p>
          </Clause>
          <Clause n={36} title="Termination">
            <p>Either party may terminate this Agreement in writing on the tenant vacating, or on 3 months' written notice. We may terminate immediately for major breach.</p>
            <p><strong>Early termination in the first year:</strong> if you end our Rent Collection or Full Management service so that it finishes within the first 12 months of a tenancy we arranged, a fee equivalent to 8% + VAT of the gross rent for the remainder of that first 12-month period becomes payable on termination, in place of the monthly management commission for those months.</p>
          </Clause>
        </div>
      </Section>

      {/* Contact */}
      <Section eyebrow="Questions?" title="Get in Touch with New Space">
        <p>Whether you're ready to instruct us, have questions about which service is right for you, or simply want to talk through the Terms before accepting, our team is on hand to help.</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [MapPin, "Visit Us", "266 Kingsland Road, London E8 4DG", null],
            [Mail, "Email Us", "website@newspaceuk.com", "mailto:website@newspaceuk.com"],
            [Phone, "Call Us", "020 7275 2040", "tel:02072752040"],
            [Globe, "Online", "newspaceuk.com", "https://newspaceuk.com"],
          ].map(([Icon, label, val, href]) => (
            <div key={label} className="rounded-xl bg-[#f3ede3] p-5">
              <Icon size={20} className="text-[#35a4a4]" />
              <p className="mt-3 text-xs uppercase tracking-wider text-[#7a716a]">{label}</p>
              {href ? <a href={href} className="mt-1 block text-sm font-semibold text-[#16323f] hover:text-[#ec008c]">{val}</a> : <p className="mt-1 text-sm font-semibold text-[#16323f]">{val}</p>}
            </div>
          ))}
        </div>
        <p className="text-xs text-[#7a716a]">Managed Exit Ltd t/a New Space · Reg. No. 06631310 · VAT 975741674 · Member of The Property Ombudsman · Client Money Protection through UKALA no. 18882</p>
      </Section>

      <div className="mt-10 flex items-center justify-center gap-2 text-center">
        <ShieldCheck size={18} className="text-[#35a4a4]" />
        <p className="font-display text-lg font-600 text-[#16323f]">Ready to proceed? Accept & sign below.</p>
      </div>
    </div>
  );
}
